import React, { useReducer, useEffect, useState } from 'react';
import { AppStateContext } from './AppStateContext';
import type { AppState } from '../types/quiz';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { appReducer } from '../store/reducer';
import { initialState } from '../store/initialState';
import { CookieManager } from '../utils/CookieManager';

export const AppStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize user from stored auth data
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem('token');
        const authData = CookieManager.get('authToken');
        
        if (token && authData) {
          const userData = JSON.parse(decodeURIComponent(authData));
          dispatch({ 
            type: 'LOGIN_SUCCESS', 
            payload: { 
              token: token,
              user: userData.user 
            }
            

          });
        }
      } catch {
        localStorage.removeItem('token');
        CookieManager.remove('authToken');
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // Keep a lightweight global copy of the app state for non-hook consumers that run
  // outside React components (helpers, utilities). Updated on every state change.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__APP_STATE__ = state;
    }
  }, [state]);

  // Don't render children until auth state is initialized
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }
  return (
    <AppStateContext.Provider value={{ state, dispatch }}>
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </AppStateContext.Provider>
  );
};

// Provide a typed global reference for read-only access in non-React modules.
declare global {
  interface Window {
    __APP_STATE__?: AppState;
  }
}

// Keep a lightweight global copy of the app state for non-hook consumers that run
// outside React components (helpers, utilities). Updated on every state change.
// Note: we also update the global from inside the provider on each render.